import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { httpClient } from "@/shared/api/http-client";
import type {
  CreateDocumentTypeInput,
  TosDocumentType,
  UpdateDocumentTypeInput,
} from "../shared/compliance-types";

export const DOCUMENT_TYPES_QUERY_KEY = ["compliance", "document-types"] as const;

// Hand-written `httpClient` calls per TD-07 — the Orval mutator
// emits the wrong response envelope, which the rest of the codebase
// works around the same way (see features/venues-approval/*).
async function fetchDocumentTypes(): Promise<readonly TosDocumentType[]> {
  return httpClient<readonly TosDocumentType[]>("/api/admin/tos/document-types");
}

export function useDocumentTypes() {
  return useQuery({
    queryKey: DOCUMENT_TYPES_QUERY_KEY,
    queryFn: fetchDocumentTypes,
    staleTime: 60_000,
  });
}

async function createDocumentType(input: CreateDocumentTypeInput): Promise<TosDocumentType> {
  return httpClient<TosDocumentType>("/api/admin/tos/document-types", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
}

export function useCreateDocumentType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createDocumentType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DOCUMENT_TYPES_QUERY_KEY });
    },
  });
}

interface UpdateDocumentTypeParams {
  readonly id: string;
  readonly input: UpdateDocumentTypeInput;
}

async function updateDocumentType({
  id,
  input,
}: UpdateDocumentTypeParams): Promise<TosDocumentType> {
  return httpClient<TosDocumentType>(`/api/admin/tos/document-types/${id}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
}

export function useUpdateDocumentType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateDocumentType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DOCUMENT_TYPES_QUERY_KEY });
    },
  });
}

async function deleteDocumentType(id: string): Promise<void> {
  await httpClient<void>(`/api/admin/tos/document-types/${id}`, {
    method: "DELETE",
  });
}

export function useDeleteDocumentType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteDocumentType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DOCUMENT_TYPES_QUERY_KEY });
    },
  });
}
